import { isEmpty } from 'lodash';
import { CalendarRange } from 'lucide-react';

import { useSavedItems } from '@/hooks/use-saved-items';
import { SAVED_ITEM_TYPE, SCHEDULE_SEARCH_URL } from '@/lib/constants';
import { SavedItemModel } from '@/lib/models/saved-item/types';
import { getSavedItemLink } from '@/lib/models/saved-item/utils';
import { formatDateTimeForUser } from '@/utils/format/date-time';
import { trackUser } from '@/utils/tracking';

import { DashboardCard } from '../card';
import { DashboardEmpty } from '../empty';
import { DashboardListItem } from '../list-item';

const DashboardSavedSchedules = () => {
  const { savedItems, deleteSavedItem } = useSavedItems();

  const filteredItems = savedItems.filter((item) => item.type === SAVED_ITEM_TYPE.SCHEDULE);
  const hasItems = !isEmpty(filteredItems);

  const handleClick = (item: SavedItemModel) => {
    trackUser.event(`Clicked saved item`, { label: item.label, type: item.type, from: 'Dashboard' });
  };

  const handleStarClick = (item: SavedItemModel) => {
    deleteSavedItem(item);
  };

  return (
    <DashboardCard
      title="Starred Schedules"
      icon={<CalendarRange />}
      ctaHref={hasItems ? SCHEDULE_SEARCH_URL : ''}
      ctaText="New schedule search"
    >
      {hasItems && (
        <ul>
          {filteredItems.map((item) => {
            const dataObject = item.data || {};
            const { dateFrom, dateTo } = dataObject;
            const isDateRange = Boolean(dateFrom && dateTo);

            return (
              <DashboardListItem
                key={item.id}
                title={item.label}
                href={getSavedItemLink(item)}
                isSaved
                description={
                  isDateRange ? (
                    `${formatDateTimeForUser(dateFrom)} → ${formatDateTimeForUser(dateTo)}`
                  ) : (
                    <span>
                      {dateFrom
                        ? `Departing: ${formatDateTimeForUser(dateFrom)}`
                        : `Arriving: ${formatDateTimeForUser(dateTo)}`}
                    </span>
                  )
                }
                onItemClick={() => handleClick(item)}
                onStarClick={() => handleStarClick(item)}
              />
            );
          })}
        </ul>
      )}
      {!hasItems && (
        <DashboardEmpty
          title="You have no saved schedule searches"
          cta="Explore Schedules"
          href={SCHEDULE_SEARCH_URL}
        />
      )}
    </DashboardCard>
  );
};

export { DashboardSavedSchedules };
